import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  Bell,
  BellOff,
  Bot,
  CalendarDays,
  CheckCircle2,
  ClipboardCopy,
  Puzzle,
  DatabaseZap,
  ExternalLink,
  HardDriveDownload,
  Mail,
  RefreshCw,
  Shield,
  ShieldCheck,
  Sparkles,
  Unplug,
} from 'lucide-react';
import {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from '../router';
import {api, fetchAuthenticatedExtensionPackage} from '../api';
import {useWorkOperation} from '../hooks/useWorkOperation';
import {BetaNoticeCopy} from '../components/BetaNotice';
import {PrimaryButton, QuietButton, SecondaryButton, Surface} from '../design';
import {CapabilityName, IntegrationStatus} from '../types';
import {EvidenceCapture} from '../components/EvidenceCapture';
import {ErrorPanel, LoadingPanel, PageHeader, formatDateTime, formatRelativeDate} from '../components/PagePrimitives';
import {CalendarEventManager} from '../components/CalendarEventManager';

const metadata: Record<
  IntegrationStatus['provider'],
  {title: string; description: string; icon: typeof Mail}
> = {
  gmail: {
    title: 'Gmail',
    description: 'Reads recruiter threads and applies only custom PursuitDeck labels. It cannot draft, send, archive, delete, star, or change read state.',
    icon: Mail,
  },
  calendar: {
    title: 'Google Calendar',
    description: 'Creates editable private event proposals with no attendees, invitations, or RSVP actions.',
    icon: CalendarDays,
  },
  apify: {
    title: 'Job discovery',
    description: 'Runs private-beta LinkedIn and Indeed discovery with a $15 warning and a $20 monthly hard stop.',
    icon: DatabaseZap,
  },
  openai: {
    title: 'Career intelligence',
    description: 'Uses configured OpenAI models for structured extraction and drafting. Provider content is not used as the product source of truth.',
    icon: Sparkles,
  },
  extension: {
    title: 'Chrome companion',
    description: 'Inspects only the active application tab after you invoke it and requires approval before every page submission.',
    icon: Puzzle,
  },
};

const providerCapability: Record<IntegrationStatus['provider'], CapabilityName> = {
  gmail: 'gmail',
  calendar: 'calendar',
  apify: 'discovery',
  openai: 'ai',
  extension: 'chromeCompanion',
};

const safeHttpsLink = (value: string | undefined): string | null => {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && !parsed.username && !parsed.password
      ? parsed.toString()
      : null;
  } catch {
    return null;
  }
};

const GOOGLE_CALLBACK_MESSAGES: Record<string, string> = {
  declined: 'Google authorization was declined. Connect again to grant access.',
  invalid_response: 'Google returned an authorization response PursuitDeck could not read. Connect again.',
  exchange_failed: 'Google authorization could not be completed. Connect again.',
  access_revoked: 'Your PursuitDeck access changed while connecting. Sign in again, then reconnect.',
  account_changed: 'That Google account does not match your signed-in account. Reconnect with the matching account.',
  workspace_deleted: 'This PursuitDeck workspace was permanently deleted.',
  unconfigured: 'Google connections are not configured for this installation.',
  failed: 'Google authorization could not be completed. Connect again.',
};

export const SettingsPage = ({recoverySessionId = null, googleError = null}: {
  recoverySessionId?: string | null;
  googleError?: string | null;
} = {}) => {
  const queryClient = useQueryClient();
  const [showNotice, setShowNotice] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [calendarEventsOpen, setCalendarEventsOpen] = useState(false);
  const [gmailOperationId, setGmailOperationId] = useState<string | null>(null);
  const [tokenStatus, setTokenStatus] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const recoveryPackage = useQuery({
    queryKey: ['extension-manual-recovery', recoverySessionId],
    queryFn: () => api.manualRecoveryPackage(recoverySessionId as string),
    enabled: Boolean(recoverySessionId),
  });
  const bootstrap = useQuery({queryKey: ['bootstrap'], queryFn: api.bootstrap});
  const integrations = useQuery({queryKey: ['integrations'], queryFn: api.integrations});
  useEffect(() => {
    if (!recoverySessionId || integrations.isLoading) return;
    const target = document.getElementById('browser-companion');
    if (typeof target?.scrollIntoView === 'function') {
      target.scrollIntoView({block: 'center'});
    }
    target?.focus({preventScroll: true});
  }, [integrations.isLoading, recoverySessionId]);

  const sync = useMutation({
    mutationFn: api.syncGmail,
    onSuccess: (result) => setGmailOperationId(result.operationId),
  });
  const gmailInvalidations = useMemo(() => [
    ['integrations'],
    ['bootstrap'],
    ['opportunities'],
    ['relationships'],
    ['action-items'],
  ] as const, []);
  const gmailOperation = useWorkOperation(gmailOperationId, gmailInvalidations);
  const gmailSyncActive = gmailOperation.data?.state === 'queued' || gmailOperation.data?.state === 'running';
  const labelRule = useMutation({
    mutationFn: api.setGmailLabelRule,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['integrations']}),
        queryClient.invalidateQueries({queryKey: ['action-items']}),
        queryClient.invalidateQueries({queryKey: ['bootstrap']}),
      ]);
    },
  });
  const disconnect = useMutation({
    mutationFn: api.disconnectIntegration,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['integrations']}),
        queryClient.invalidateQueries({queryKey: ['action-items']}),
        queryClient.invalidateQueries({queryKey: ['bootstrap']}),
      ]);
    },
  });
  const notificationConsent = useMutation({
    mutationFn: api.updateNotificationConsent,
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['bootstrap']});
    },
  });
  const connectGoogle = useMutation({
    mutationFn: async (service: 'gmail' | 'calendar') => {
      const {authorizationUrl} = await api.startGoogleOAuth(service);
      const target = new URL(authorizationUrl);
      if (target.protocol !== 'https:' || target.hostname !== 'accounts.google.com') {
        throw new Error('Google returned an invalid authorization URL.');
      }
      return target.toString();
    },
    onSuccess: (authorizationUrl) => window.location.assign(authorizationUrl),
  });
  const downloadExtension = useMutation({
    mutationFn: async () => {
      const blob = await fetchAuthenticatedExtensionPackage();
      const objectUrl = URL.createObjectURL(blob);
      try {
        const link = window.document.createElement('a');
        link.href = objectUrl;
        link.download = 'pursuitdeck-extension.zip';
        link.click();
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    },
  });
  const copyExtensionGrant = useMutation({
    mutationFn: async () => {
      setTokenStatus('');
      const {grant} = await api.issueExtensionGrant();
      await navigator.clipboard.writeText(grant);
    },
    onSuccess: () => setTokenStatus('One-use grant copied. Paste it into the companion within five minutes.'),
    onError: () => setTokenStatus('The one-use grant could not be copied. Sign in again and retry.'),
  });

  const copyAnswer = async (fieldId: string, value: string): Promise<void> => {
    setCopyStatus('');
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus('Copied answer for ' + fieldId + '.');
    } catch {
      setCopyStatus('That answer could not be copied. Select the text and copy it manually.');
    }
  };

  const manualPackage = recoveryPackage.data?.manualPackage;
  const directApplicationUrl = safeHttpsLink(manualPackage?.directUrl);

  return (
    <>
      <PageHeader
        eyebrow="Connections and boundaries"
        title="Know exactly what PursuitDeck can do."
        description="Integrations are incremental, revocable, and narrower in product behavior than some provider scopes imply. External writes always preserve the approval boundary."
        actions={<div className="pd-detail-actions">
          <PrimaryButton leftSection={<Sparkles size={16} />} onClick={() => setEvidenceOpen(true)}>
            Add career evidence
          </PrimaryButton>
          <SecondaryButton component="a" href="/onboarding">
            Career interview
          </SecondaryButton>
        </div>}
      />

      {integrations.isLoading ? <LoadingPanel label="Loading integrations" /> : null}
      {integrations.isError ? <ErrorPanel error={integrations.error} retry={() => void integrations.refetch()} /> : null}

      <section className="pd-settings-section" aria-labelledby="notification-heading">
        <div className="pd-section-heading">
          <div>
            <span className="pd-eyebrow">Notifications</span>
            <h3 id="notification-heading">Email only with your permission</h3>
          </div>
        </div>
        <Surface>
          <div className="pd-integration-header">
            <div className="pd-integration-icon" aria-hidden="true">
              {bootstrap.data?.notifications.state === 'enabled' ? <Bell size={22} /> : <BellOff size={22} />}
            </div>
            <div>
              <h4>Candidate action reminders</h4>
              <span className={'pd-integration-state state-' + (bootstrap.data?.notifications.state ?? 'disabled')}>
                {bootstrap.data?.notifications.state ?? 'disabled'}
              </span>
            </div>
          </div>
          <p>
            Your sign-in email identifies your account. PursuitDeck will send candidate reminders only after you
            explicitly enable them here, and disabling them stops future candidate reminder sends.
          </p>
          {bootstrap.data?.notifications.selectedEmail ? (
            <small>Selected verified sign-in address: {bootstrap.data.notifications.selectedEmail}</small>
          ) : null}
          {bootstrap.data?.capabilities?.notifications.available === false ? (
            <div className="pd-integration-message" role="status">
              {bootstrap.data.capabilities.notifications.message} Your preference is still saved for when delivery becomes available.
            </div>
          ) : null}
          <div className="pd-integration-actions">
            {bootstrap.data?.notifications.state === 'enabled' ? (
              <QuietButton
                color="red"
                loading={notificationConsent.isPending}
                onClick={() => notificationConsent.mutate('disabled')}
              >
                Disable email reminders
              </QuietButton>
            ) : (
              <PrimaryButton
                loading={notificationConsent.isPending}
                onClick={() => notificationConsent.mutate('enabled')}
              >
                Enable email reminders
              </PrimaryButton>
            )}
          </div>
          {notificationConsent.isError ? (
            <p className="pd-form-error" role="alert">The notification preference could not be saved. Try again.</p>
          ) : null}
        </Surface>
      </section>

      <section className="pd-settings-section" aria-labelledby="integration-heading">
        <div className="pd-section-heading">
          <div>
            <span className="pd-eyebrow">Integrations</span>
            <h3 id="integration-heading">Connected services</h3>
          </div>
        </div>
        <div className="pd-integration-grid">
          {integrations.data?.map((integration) => {
            const item = metadata[integration.provider];
            const Icon = item.icon;
            const capability = bootstrap.data?.capabilities[providerCapability[integration.provider]];
            const gmailCredentialsAvailable = integration.provider === 'gmail' && ['connected', 'stale'].includes(integration.state);
            const googleCredentialsAvailable = integration.state === 'connected' || gmailCredentialsAvailable;
            const googleProvider = integration.provider === 'gmail' || integration.provider === 'calendar';
            const authorizationExpiresAt = integration.authorizationExpiresAt
              ? Date.parse(integration.authorizationExpiresAt)
              : Number.NaN;
            const authorizationExpiring = googleProvider
              && Number.isFinite(authorizationExpiresAt)
              && authorizationExpiresAt <= Date.now() + 2 * 24 * 60 * 60 * 1_000;
            return (
              <Surface
                key={integration.provider}
                id={integration.provider === 'extension' ? 'browser-companion' : undefined}
                tabIndex={integration.provider === 'extension' && recoverySessionId ? -1 : undefined}
                className={
                  'pd-integration-card' +
                  (integration.provider === 'extension' && recoverySessionId ? ' is-recovery-target' : '')
                }
              >
                <div className="pd-integration-header">
                  <div className="pd-integration-icon" aria-hidden="true">
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <span className={'pd-integration-state state-' + integration.state}>
                      {integration.state === 'connected' ? <CheckCircle2 size={13} aria-hidden="true" /> : null}
                      {integration.state}
                    </span>
                  </div>
                </div>
                <p>{item.description}</p>
                {capability?.available === false ? (
                  <div className="pd-integration-message" role="status">{capability.message}</div>
                ) : null}
                {integration.lastSuccessfulAt ? (
                  <small>Last successful activity {formatRelativeDate(integration.lastSuccessfulAt)}</small>
                ) : null}
                {integration.message ? <div className="pd-integration-message">{integration.message}</div> : null}
                {authorizationExpiring && integration.authorizationExpiresAt ? (
                  <div className="pd-stale-banner" role="alert">
                    <div>
                      <strong>{integration.reconnectRequired ? 'Google authorization expired.' : 'Google authorization expires soon.'}</strong>
                      <span>
                        {integration.reconnectRequired
                          ? 'Reconnect before using dependent features.'
                          : `Reconnect by ${formatDateTime(integration.authorizationExpiresAt)} to avoid an interruption.`}
                      </span>
                    </div>
                  </div>
                ) : null}
                {integration.provider === 'extension' && recoverySessionId ? (
                  <div
                    className="pd-manual-recovery-callout"
                    role="region"
                    aria-labelledby="manual-recovery-heading"
                  >
                    <div className="pd-manual-recovery-header">
                      <div>
                        <strong id="manual-recovery-heading">Manual application step required</strong>
                        <span>Recovery reference <code>{recoverySessionId.slice(0, 12)}</code></span>
                      </div>
                      {directApplicationUrl ? (
                        <a
                          className="pd-manual-recovery-link"
                          href={directApplicationUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          Open application
                          <ExternalLink size={14} aria-hidden="true" />
                        </a>
                      ) : null}
                    </div>

                    {recoveryPackage.isLoading ? <p>Loading the saved application package…</p> : null}
                    {recoveryPackage.isError ? (
                      <div className="pd-manual-recovery-error" role="alert">
                        <p>
                          {recoveryPackage.error instanceof Error
                            ? recoveryPackage.error.message
                            : 'The saved application package could not be loaded.'}
                        </p>
                        <QuietButton onClick={() => void recoveryPackage.refetch()}>Retry</QuietButton>
                      </div>
                    ) : null}

                    {manualPackage ? (
                      <div className="pd-manual-recovery-body">
                        <p>{manualPackage.reason}</p>
                        {!directApplicationUrl ? (
                          <p className="pd-manual-recovery-warning" role="alert">
                            The saved application link is unavailable because it is not a valid HTTPS address.
                          </p>
                        ) : null}

                        {manualPackage.answers.length ? (
                          <section aria-labelledby="manual-recovery-answers">
                            <h5 id="manual-recovery-answers">Saved answers</h5>
                            <div className="pd-manual-answer-list">
                              {manualPackage.answers.map((answer) => (
                                <article className="pd-manual-answer" key={answer.fieldId}>
                                  <div>
                                    <strong>{answer.label}</strong>
                                    <QuietButton
                                      aria-label={'Copy answer for ' + answer.label}
                                      leftSection={<ClipboardCopy size={14} aria-hidden="true" />}
                                      onClick={() => void copyAnswer(answer.fieldId, answer.value)}
                                    >
                                      Copy
                                    </QuietButton>
                                  </div>
                                  <pre tabIndex={0}>{answer.value}</pre>
                                </article>
                              ))}
                            </div>
                            {copyStatus ? <p role="status" aria-live="polite">{copyStatus}</p> : null}
                          </section>
                        ) : null}

                        {manualPackage.files.length ? (
                          <section aria-labelledby="manual-recovery-files">
                            <h5 id="manual-recovery-files">Files to attach</h5>
                            <div className="pd-manual-file-list">
                              {manualPackage.files.map((file) => {
                                const downloadUrl = safeHttpsLink(file.downloadUrl);
                                return (
                                  <div className="pd-manual-file" key={file.fieldId}>
                                    <div>
                                      <strong>{file.filename}</strong>
                                      <span>{file.label}</span>
                                    </div>
                                    {downloadUrl ? (
                                      <a href={downloadUrl} target="_blank" rel="noreferrer noopener">
                                        <HardDriveDownload size={14} aria-hidden="true" />
                                        Download file
                                      </a>
                                    ) : (
                                      <span>Download unavailable; choose this filename manually.</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </section>
                        ) : null}

                        <section aria-labelledby="manual-recovery-steps">
                          <h5 id="manual-recovery-steps">Finish safely</h5>
                          <ol>
                            {manualPackage.steps.map((step, index) => (
                              <li key={index}>{step}</li>
                            ))}
                          </ol>
                        </section>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {gmailCredentialsAvailable ? (
                  <div className={'pd-gmail-rule ' + (integration.labelStandingRuleApprovedAt ? 'is-approved' : 'is-pending')}>
                    <ShieldCheck size={20} aria-hidden="true" />
                    <div>
                      <strong>
                        {integration.labelStandingRuleApprovedAt
                          ? 'Automatic label rule approved'
                          : 'Approve automatic recruiter labels'}
                      </strong>
                      <p>
                        {integration.labelStandingRuleApprovedAt
                          ? 'PursuitDeck may automatically apply only its own PursuitDeck/ labels to threads that match recruiter addresses you explicitly saved. Revoke this rule at any time without disconnecting Gmail.'
                          : 'Approval lets PursuitDeck create, apply, or remove only labels beginning PursuitDeck/ on threads that match recruiter addresses you explicitly saved. It cannot send, draft, reply, forward, archive, delete, star, change importance, or change read state.'}
                      </p>
                      {integration.labelStandingRuleApprovedAt ? (
                        <QuietButton
                          color="red"
                          loading={labelRule.isPending && labelRule.variables === false}
                          onClick={() => labelRule.mutate(false)}
                        >
                          Revoke automatic labels
                        </QuietButton>
                      ) : (
                        <SecondaryButton
                          loading={labelRule.isPending && labelRule.variables === true}
                          onClick={() => labelRule.mutate(true)}
                        >
                          Approve automatic labels
                        </SecondaryButton>
                      )}
                    </div>
                  </div>
                ) : null}
                <div className="pd-integration-actions">
                  {gmailCredentialsAvailable && capability?.usable !== false ? (
                    <SecondaryButton
                      loading={sync.isPending || gmailSyncActive}
                      leftSection={<RefreshCw size={15} aria-hidden="true" />}
                      onClick={() => sync.mutate()}
                    >
                      Sync now
                    </SecondaryButton>
                  ) : null}
                  {integration.provider === 'gmail' && gmailOperation.data && gmailOperation.data.state !== 'succeeded' ? (
                    <p className={gmailOperation.data.state.startsWith('failed') ? 'pd-form-error' : 'pd-inline-status'} role={gmailOperation.data.state.startsWith('failed') ? 'alert' : 'status'}>
                      {gmailOperation.data.state === 'running'
                        ? 'Gmail synchronization is running.'
                        : gmailOperation.data.state === 'queued'
                          ? 'Gmail synchronization is queued.'
                          : gmailOperation.data.message ?? 'Gmail synchronization could not finish.'}
                    </p>
                  ) : null}
                  {integration.provider === 'calendar' && integration.state === 'connected' && capability?.usable !== false ? (
                    <SecondaryButton
                      leftSection={<CalendarDays size={15} aria-hidden="true" />}
                      onClick={() => setCalendarEventsOpen(true)}
                    >
                      Manage private events
                    </SecondaryButton>
                  ) : null}
                  {(integration.provider === 'gmail' || integration.provider === 'calendar') &&
                  !googleCredentialsAvailable && capability?.available !== false ? (
                    <PrimaryButton
                      loading={connectGoogle.isPending && connectGoogle.variables === integration.provider}
                      rightSection={<ExternalLink size={14} aria-hidden="true" />}
                      onClick={() => connectGoogle.mutate(integration.provider === 'gmail' ? 'gmail' : 'calendar')}
                    >
                      Connect
                    </PrimaryButton>
                  ) : null}
                  {googleProvider && googleCredentialsAvailable && authorizationExpiring ? (
                    <SecondaryButton
                      loading={connectGoogle.isPending && connectGoogle.variables === integration.provider}
                      leftSection={<RefreshCw size={15} aria-hidden="true" />}
                      onClick={() => connectGoogle.mutate(integration.provider === 'gmail' ? 'gmail' : 'calendar')}
                    >
                      Reconnect now
                    </SecondaryButton>
                  ) : null}
                  {integration.provider === 'extension' && capability?.available !== false ? (
                    <PrimaryButton
                      loading={downloadExtension.isPending}
                      leftSection={<HardDriveDownload size={15} aria-hidden="true" />}
                      onClick={() => downloadExtension.mutate()}
                    >
                      Download private package
                    </PrimaryButton>
                  ) : null}
                  {integration.provider === 'extension' && capability?.available !== false ? (
                    <SecondaryButton
                      loading={copyExtensionGrant.isPending}
                      leftSection={<ClipboardCopy size={15} aria-hidden="true" />}
                      onClick={() => copyExtensionGrant.mutate()}
                    >
                      Copy one-use connection grant
                    </SecondaryButton>
                  ) : null}
                  {googleCredentialsAvailable &&
                  (integration.provider === 'gmail' || integration.provider === 'calendar') ? (
                    <QuietButton
                      color="red"
                      loading={disconnect.isPending && disconnect.variables === integration.provider}
                      leftSection={<Unplug size={15} aria-hidden="true" />}
                      onClick={() => disconnect.mutate(integration.provider)}
                    >
                      Disconnect
                    </QuietButton>
                  ) : null}
                </div>
                {integration.provider === 'extension' ? (
                  <div className="pd-integration-message">
                    <strong>Temporary and sensitive.</strong> Copy a new opaque grant immediately before every
                    Start, Rescan, Resume, or Approve action. The companion rotates it after each server exchange,
                    keeps it only in memory for that action, and never stores it.
                    {tokenStatus ? <p role="status" aria-live="polite">{tokenStatus}</p> : null}
                  </div>
                ) : null}
              </Surface>
            );
          })}
        </div>
      </section>

      <section className="pd-settings-section" aria-labelledby="privacy-heading">
        <div className="pd-section-heading">
          <div>
            <span className="pd-eyebrow">Privacy and control</span>
            <h3 id="privacy-heading">Candidate-control commitments</h3>
          </div>
        </div>
        <div className="pd-control-grid">
          <Surface>
            <Shield size={22} aria-hidden="true" />
            <h4>Page-by-page approval</h4>
            <p>Every application page shows all proposed values and attachments before the companion fills and advances it.</p>
          </Surface>
          <Surface>
            <Bot size={22} aria-hidden="true" />
            <h4>No invented claims</h4>
            <p>Tailored résumé bullets can use only exact approved versions linked to confirmed facts and evidence.</p>
          </Surface>
          <Surface>
            <Mail size={22} aria-hidden="true" />
            <h4>No email sending</h4>
            <p>PursuitDeck can prepare copyable text but has no product path for drafting, sending, replying, or forwarding in Gmail.</p>
          </Surface>
        </div>
      </section>

      <section className="pd-settings-section" aria-labelledby="beta-heading">
        <div className="pd-section-heading">
          <div>
            <span className="pd-eyebrow">Private beta</span>
            <h3 id="beta-heading">Notice and data handling</h3>
          </div>
        </div>
        <Surface className="pd-beta-settings">
          <div>
            <h4>Retention and operator access</h4>
            <p>
              Durable job-search data is retained until an explicit deletion request. Disconnecting Google deletes refresh
              credentials immediately but leaves imported records unless you request deletion.
            </p>
            <a className="pd-text-link" href="/privacy">Read privacy and data handling details</a>
          </div>
          <SecondaryButton onClick={() => setShowNotice((value) => !value)}>
            {showNotice ? 'Hide beta notice' : 'Read beta notice'}
          </SecondaryButton>
          {showNotice ? <BetaNoticeCopy /> : null}
        </Surface>
      </section>

      <CalendarEventManager
        opened={calendarEventsOpen}
        onClose={() => setCalendarEventsOpen(false)}
      />

      <EvidenceCapture
        opened={evidenceOpen}
        onClose={() => setEvidenceOpen(false)}
        onSaved={async () => {
          await Promise.all([
            queryClient.invalidateQueries({queryKey: ['career-facts']}),
            queryClient.invalidateQueries({queryKey: ['career-bullets']}),
            queryClient.invalidateQueries({queryKey: ['documents']}),
          ]);
        }}
      />

      {googleError ? (
        <p className="pd-toast pd-toast--error" role="alert">
          {GOOGLE_CALLBACK_MESSAGES[googleError] ?? GOOGLE_CALLBACK_MESSAGES.failed}
        </p>
      ) : null}

      {(sync.isError || labelRule.isError || disconnect.isError || connectGoogle.isError || downloadExtension.isError) ? (
        <p className="pd-toast pd-toast--error" role="alert">
          {(sync.error ?? labelRule.error ?? disconnect.error ?? connectGoogle.error ?? downloadExtension.error) instanceof Error
            ? (sync.error ?? labelRule.error ?? disconnect.error ?? connectGoogle.error ?? downloadExtension.error as Error).message
            : 'The integration change could not be completed.'}
        </p>
      ) : null}
    </>
  );
};


export const SettingsRoutePage = () => {
  const [searchParams] = useSearchParams();
  const recoverySessionId =
    searchParams.get('focus') === 'extension' ? searchParams.get('recovery') : null;
  return <SettingsPage recoverySessionId={recoverySessionId} googleError={searchParams.get('googleError')} />;
};
